import type { MouseEvent, PointerEvent } from 'react';

import {
  useBoardEditingStore,
  useBoardSelectionStore,
  type BoardElementType,
  type WallBoard,
  type WallCard,
} from '@walls/store';
import { useConnectionCommands } from '../../connections/useConnectionCommands';
import { isImageCard } from '../utils/cardDisplay';

interface UseCardSelectionOptions {
  board: WallBoard;
  canEdit: boolean;
  canSelect: boolean;
  card: WallCard;
  elementType: BoardElementType;
}

export function useCardSelection({
  board,
  canEdit,
  canSelect,
  card,
  elementType,
}: UseCardSelectionOptions) {
  const connectingFromId = useBoardSelectionStore((state) => state.connectingFromId);
  const selectedIds = useBoardSelectionStore((state) => state.selectedIds);
  const currentElement = useBoardSelectionStore((state) => state.currentElement);
  const setConnectingFromId = useBoardSelectionStore((state) => state.setConnectingFromId);
  const setCurrentElement = useBoardSelectionStore((state) => state.setCurrentElement);
  const setSelectedIds = useBoardSelectionStore((state) => state.setSelectedIds);
  const toggleSelectedId = useBoardSelectionStore((state) => state.toggleSelectedId);
  const startEditCard = useBoardEditingStore((state) => state.startEditCard);
  const startEditImage = useBoardEditingStore((state) => state.startEditImage);
  const { insertConnection } = useConnectionCommands(board);

  return {
    isCurrent: currentElement?.id === card.id,
    isSelected: selectedIds.includes(card.id),
    onDoubleClick: (event: MouseEvent<HTMLElement>) => {
      event.stopPropagation();

      if (!canEdit) {
        return;
      }

      if (isImageCard(card)) {
        startEditImage(card.id);
      } else {
        startEditCard(card.id);
      }
    },
    onPointerDown: (event: PointerEvent<HTMLElement>) => {
      event.stopPropagation();

      if (!canSelect) {
        return;
      }

      if (connectingFromId) {
        if (connectingFromId === card.id) {
          setConnectingFromId(null);
        } else {
          void insertConnection(connectingFromId, card.id);
        }

        return;
      }

      setCurrentElement({ id: card.id, type: elementType });

      if (event.metaKey || event.ctrlKey) {
        toggleSelectedId(card.id);
      } else if (selectedIds.includes(card.id)) {
        return;
      } else {
        setSelectedIds([]);
      }
    },
  };
}
